// NavbarCell.tsx

import { Link } from 'react-router-dom';
import { Box, Skeleton, Theme, Typography, styled, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  ACTIVITY_LIBRARY_ROUTE,
  CALENDAR_SETTINGS_ROUTE,
  PAYMENT_SETTINGS_ROUTE,
  SIGNUP_ROUTE,
} from '../../utils/routes';
import {
  setActivityLibraryBlock,
  clearFolder,
} from '../../redux/activityLibrary/activityLibrarySlice';
import { useAppSelector } from '../../redux/hooks';
import { logout } from '../../redux/GlobalAction';
import { showPaymentInfo } from '../../utils/paymentUtills';

interface INavbarCellProps {
  title: string;
  url: string;
  src: string;
  isSelected: boolean;
  boldIcon: string;
  subtitle?: string;
  setHidden: React.Dispatch<React.SetStateAction<boolean>>;
}

const StyledBox = styled(Box)(({ theme, isSelected }: { theme: Theme; isSelected: boolean }) => ({
  '.navbarLink': {
    padding: '16px',
    height: '100%',
    width: '100%',
    display: 'flex',
    minWidth: 'auto',
    gap: '8px',
    alignItems: 'flex-start',
    backgroundColor: isSelected ? 'rgba(124, 174, 190, 0.2)' : 'transparent',
    borderLeft: isSelected ? `rgb(93,171,201, 0.5) 1px solid` : 'none',
    borderRadius: '2px',
    fontWeight: isSelected ? theme.typography.bodyBold : theme.typography.bodyRegular,
    '&:hover': {
      backgroundColor: 'rgb(93,171,201,0.1)',
      color: theme.palette.primary.dark,
      // borderLeft: '2px solid',
      transition: 'all 0.2s ease-in-out',
    },
  },
}));

const NavbarCell = ({
  isSelected,
  url,
  src,
  title,
  boldIcon,
  subtitle,
  setHidden,
}: INavbarCellProps) => {
  const { t } = useTranslation();
  const [isImageLoading, setIsImageLoading] = useState(false);
  const { coach } = useAppSelector((state) => state.coach);
  const coachConfigurations = useAppSelector((state) => state.booking.coachConfigurations);
  const theme = useTheme();
  const dispatch = useDispatch();
  const handleOnClick = () => {
    if (url === SIGNUP_ROUTE) {
      dispatch(logout());
    }
    if (url === ACTIVITY_LIBRARY_ROUTE) {
      dispatch(setActivityLibraryBlock());
      dispatch(clearFolder());
    }
    setHidden(true);
  };

  if (url === CALENDAR_SETTINGS_ROUTE && coachConfigurations.activeStep >= 0) {
    return null;
  }
  if (
    url === PAYMENT_SETTINGS_ROUTE &&
    !showPaymentInfo(coach.subscriptionPlanStatus?.currentPlan, coach.previousPlan)
  ) {
    return null;
  }

  return (
    <StyledBox isSelected={isSelected} theme={theme} onClick={handleOnClick}>
      <Link to={url} className="navbarLink">
        {isImageLoading && <Skeleton variant="rectangular" width="15px" height="15px" />}
        <Box display="flex" flexDirection="column" alignItems="center">
          <img
            src={isSelected ? boldIcon : src}
            alt="logo"
            style={{
              minWidth: '30px',
              display: isImageLoading ? 'none' : 'flex',
              width: '20px',
              height: '20px',
            }}
            onLoad={() => setIsImageLoading(false)}
          />
        </Box>

        <Box display="flex" flexDirection="column">
          <Typography
            variant={isSelected ? 'bodyBold' : 'bodyRegular'}
            color="primary"
            fontSize="15px"
            textAlign="left"
          >
            {t(title)}
          </Typography>
          {subtitle && (
            <Typography
              textOverflow="ellipsis"
              color="primary"
              textAlign="left"
              sx={{ color: 'grey', fontSize: '13px' }}
              variant={isSelected ? 'subtitle2' : 'subtitle1'}
            >
              {t(subtitle)}
            </Typography>
          )}
        </Box>
      </Link>
    </StyledBox>
  );
};

export default NavbarCell;
